import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"

import BottomSheet, {
  BottomSheetHandle,
} from "@fuse/sharedComponents/bottomSheet/BottomSheet"

export interface DescriptionBottomSheetHandle {
  openModal: () => void
  closeModal: () => void
}

const DescriptionBottomSheet = forwardRef<
  DescriptionBottomSheetHandle,
  {
    register: any
    errors: any
    onChange: (value: string) => void
    description: string
    isDefaultBg?: boolean
    onClose?: () => void
  }
>(
  (
    { register, errors, onChange, description, isDefaultBg = false, onClose },
    ref,
  ) => {
    const bottomSheetRef = useRef<BottomSheetHandle>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    // Get the registration details for the description field
    const { ref: registerRef, ...rest } = register("description")

    useImperativeHandle(ref, () => ({
      openModal: () => {
        bottomSheetRef.current?.openModal()
      },
      closeModal: () => {
        bottomSheetRef.current?.closeModal()
      },
    }))

    const handleOpen = () => {
      // Use a longer timeout to ensure DOM is fully rendered and animations complete
      setTimeout(() => {
        if (textareaRef.current) {
          // Focus and select the textarea
          textareaRef.current.focus()

          // For mobile devices, ensure the keyboard appears
          if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
            // Force focus on mobile devices
            textareaRef.current.click()
            textareaRef.current.focus()
          }

          // Move cursor to end of existing text
          const textLength = textareaRef.current.value.length
          textareaRef.current.setSelectionRange(textLength, textLength)
        }
      }, 300) // Increased timeout to ensure proper rendering
    }

    return (
      <BottomSheet
        ref={bottomSheetRef}
        title="Description"
        isDefaultBg={isDefaultBg}
        onOpen={handleOpen}
        height="h-[80vh] max-h-[80vh]"
        onClose={onClose}
      >
        <textarea
          {...rest}
          ref={(e) => {
            registerRef(e)
            textareaRef.current = e
          }}
          onChange={(e) => onChange(e.target.value)}
          className="w-full flex-1 resize-none bg-transparent text-white h-[65vh] text-base focus:outline-none overflow-y-auto no-scrollbar"
          placeholder="Write your description here"
          value={description}
          // Add these attributes to improve mobile keyboard handling
          autoFocus
          inputMode="text"
          enterKeyHint="done"
        />
      </BottomSheet>
    )
  },
)

export default DescriptionBottomSheet
